import type { AxiosPromise } from "axios";
import { type DependencyList, useCallback, useEffect, useRef, useState } from "react";

enum AxiosPromiseStatus {
  Pending = 0,
  Success = 1,
  Error = 2,
}

export const axiosSuspenseWrapper = <T>(axiosPromise: AxiosPromise<T>) => {
  let status: AxiosPromiseStatus = AxiosPromiseStatus.Pending;
  let result: T;
  let error: unknown;

  const promise = axiosPromise.then(
    (value) => {
      status = AxiosPromiseStatus.Success;
      result = value.data;
    },
    (err) => {
      status = AxiosPromiseStatus.Error;
      error = err;
    }
  );

  return () => {
    switch (status) {
      case AxiosPromiseStatus.Pending:
        throw promise;
      case AxiosPromiseStatus.Success:
        return result;
      case AxiosPromiseStatus.Error:
        throw error;
    }
  };
};

export const useSuspenseWithReload = <T>(call: () => AxiosPromise<T>, deps: DependencyList) => {
  const callRef = useRef(call);
  callRef.current = call;
  const [fn, setFn] = useState<ReturnType<typeof axiosSuspenseWrapper<T>>>(() => () => {
    throw new Promise(() => undefined);
  });

  const handleCall = useCallback(() => {
    const fn = axiosSuspenseWrapper(callRef.current());
    setFn(() => fn);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only reload on deps changing
  useEffect(() => {
    handleCall();
  }, deps);

  return [fn, handleCall] as const;
};

export const useSuspense = <T>(call: () => AxiosPromise<T>, deps: DependencyList) => {
  const [fn, setFn] = useState<ReturnType<typeof axiosSuspenseWrapper<T>>>(() => () => {
    throw new Promise(() => undefined);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: only reload on deps changing
  useEffect(() => {
    const fn = axiosSuspenseWrapper(call());
    setFn(() => fn);
  }, deps);

  return fn;
};
